import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { 
    TrendingUp, 
    TrendingDown,
    Car, 
    Users,
    Fuel,
    Package,
    AlertTriangle,
    CheckCircle,
    Info,
    Loader2,
    DollarSign,
    Gauge,
    BarChart3,
    Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';

export default function FleetOptimizationPage() {
    const { language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [cargoHistory, setCargoHistory] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('30');
    const [activeTab, setActiveTab] = useState('overview');

    const isEn = language === 'en';

    useEffect(() => {
        fetchData();
    }, [selectedPeriod]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [optimizationRes, cargoRes] = await Promise.all([
                api.get(`/fleet-optimization?days=${selectedPeriod}`),
                api.get(`/fleet-optimization/cargo-history?days=${selectedPeriod}`)
            ]);
            setData(optimizationRes.data);
            setCargoHistory(cargoRes.data);
        } catch (error) {
            console.error('Error fetching optimization data:', error);
            toast.error(isEn ? 'Error loading data' : 'Σφάλμα φόρτωσης δεδομένων');
        } finally {
            setLoading(false);
        }
    };

    const getUtilizationColor = (pct) => {
        if (pct >= 70) return 'text-green-600 bg-green-100';
        if (pct >= 40) return 'text-yellow-600 bg-yellow-100';
        return 'text-red-600 bg-red-100';
    };

    const getUtilizationBadge = (status) => {
        switch (status) {
            case 'optimal':
                return <Badge className="bg-green-100 text-green-800">{isEn ? 'Optimal' : 'Βέλτιστο'}</Badge>;
            case 'moderate':
                return <Badge className="bg-yellow-100 text-yellow-800">{isEn ? 'Moderate' : 'Μέτριο'}</Badge>;
            case 'underused':
                return <Badge className="bg-red-100 text-red-800">{isEn ? 'Underused' : 'Υποχρησιμοποιημένο'}</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const getEfficiencyBadge = (rating) => {
        switch (rating) {
            case 'excellent':
                return <Badge className="bg-green-100 text-green-800">{isEn ? 'Excellent' : 'Άριστο'}</Badge>;
            case 'good':
                return <Badge className="bg-blue-100 text-blue-800">{isEn ? 'Good' : 'Καλό'}</Badge>;
            case 'average':
                return <Badge className="bg-yellow-100 text-yellow-800">{isEn ? 'Average' : 'Μέτριο'}</Badge>;
            case 'needs_improvement':
                return <Badge className="bg-red-100 text-red-800">{isEn ? 'Needs Improvement' : 'Χρειάζεται Βελτίωση'}</Badge>;
            default:
                return <Badge variant="secondary">{rating}</Badge>;
        }
    };

    const InfoTooltip = ({ content }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button className="ml-2 text-slate-400 hover:text-[#1B7EB5] transition-colors">
                        <Info className="w-4 h-4" />
                    </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-slate-900 text-white p-3 rounded-lg">
                    <p className="text-sm">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-[#1B7EB5]" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6" data-testid="fleet-optimization-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-slate-800">
                        {isEn ? 'Fleet Optimization' : 'Βελτιστοποίηση Στόλου'}
                    </h1>
                    <InfoTooltip 
                        content={isEn 
                            ? "Fleet Optimization analyzes your vehicle usage patterns to identify underused vehicles, compare driver efficiency, and find cost-saving opportunities. Aim for 70%+ utilization for optimal fleet efficiency."
                            : "Η Βελτιστοποίηση Στόλου αναλύει τα πρότυπα χρήσης των οχημάτων σας για να εντοπίσει υποχρησιμοποιημένα οχήματα, να συγκρίνει την αποδοτικότητα των οδηγών και να βρει ευκαιρίες εξοικονόμησης κόστους."
                        }
                    />
                </div>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger className="w-40">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">{isEn ? 'Last 7 days' : 'Τελευταίες 7 μέρες'}</SelectItem>
                        <SelectItem value="30">{isEn ? 'Last 30 days' : 'Τελευταίες 30 μέρες'}</SelectItem>
                        <SelectItem value="90">{isEn ? 'Last 90 days' : 'Τελευταίες 90 μέρες'}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Summary Cards */}
            {data && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 flex items-center">
                                        {isEn ? 'Fleet Utilization' : 'Χρήση Στόλου'}
                                        <InfoTooltip 
                                            content={isEn 
                                                ? "Percentage of days your vehicles are actively used. 70%+ is optimal, below 40% suggests the vehicle may not be needed."
                                                : "Ποσοστό ημερών που τα οχήματά σας χρησιμοποιούνται ενεργά. 70%+ είναι βέλτιστο, κάτω από 40% υποδηλώνει ότι το όχημα μπορεί να μην χρειάζεται."
                                            }
                                        />
                                    </p>
                                    <p className="text-3xl font-bold text-slate-800">{data.overall_utilization}%</p>
                                </div>
                                <div className={`p-3 rounded-full ${data.overall_utilization >= 70 ? 'bg-green-100' : data.overall_utilization >= 40 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                                    <Gauge className={`w-6 h-6 ${data.overall_utilization >= 70 ? 'text-green-600' : data.overall_utilization >= 40 ? 'text-yellow-600' : 'text-red-600'}`} />
                                </div>
                            </div>
                            <Progress value={data.overall_utilization} className="mt-3 h-2" />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 flex items-center">
                                        {isEn ? 'Underused Vehicles' : 'Υποχρησιμοποιημένα'}
                                        <InfoTooltip 
                                            content={isEn 
                                                ? "Vehicles with less than 40% utilization. Consider reducing your fleet or reassigning these vehicles to save costs."
                                                : "Οχήματα με χρήση κάτω από 40%. Σκεφτείτε να μειώσετε τον στόλο σας ή να ανακατανείμετε αυτά τα οχήματα για εξοικονόμηση."
                                            }
                                        />
                                    </p>
                                    <p className="text-3xl font-bold text-slate-800">{data.underused_vehicles}</p>
                                </div>
                                <div className={`p-3 rounded-full ${data.underused_vehicles > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
                                    <AlertTriangle className={`w-6 h-6 ${data.underused_vehicles > 0 ? 'text-orange-600' : 'text-green-600'}`} />
                                </div>
                            </div>
                            {data.underused_vehicles > 0 && (
                                <p className="text-xs text-orange-600 mt-2">
                                    {isEn ? 'Review these vehicles' : 'Ελέγξτε αυτά τα οχήματα'}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 flex items-center">
                                        {isEn ? 'Potential Savings' : 'Πιθανή Εξοικονόμηση'}
                                        <InfoTooltip 
                                            content={isEn 
                                                ? "Estimated monthly savings if you remove underused vehicles from your fleet. Based on your plan's per-vehicle pricing."
                                                : "Εκτιμώμενη μηνιαία εξοικονόμηση εάν αφαιρέσετε τα υποχρησιμοποιημένα οχήματα. Βασίζεται στην τιμή ανά όχημα του πλάνου σας."
                                            }
                                        />
                                    </p>
                                    <p className="text-3xl font-bold text-green-600">€{data.potential_monthly_savings}</p>
                                </div>
                                <div className="p-3 rounded-full bg-green-100">
                                    <DollarSign className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">{isEn ? '/month' : '/μήνα'}</p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500 flex items-center">
                                        {isEn ? 'Avg Cargo Capacity' : 'Μέση Χωρητικότητα'}
                                        <InfoTooltip 
                                            content={isEn 
                                                ? "Average cargo space utilization based on AI analysis of driver photos. Higher % means better trip efficiency - fewer trips needed."
                                                : "Μέση χρήση χώρου φορτίου βάσει ανάλυσης AI των φωτογραφιών οδηγών. Υψηλότερο % σημαίνει καλύτερη αποδοτικότητα - λιγότερα ταξίδια."
                                            }
                                        />
                                    </p>
                                    <p className="text-3xl font-bold text-slate-800">
                                        {data.avg_cargo_capacity !== null ? `${data.avg_cargo_capacity}%` : 'N/A'}
                                    </p>
                                </div>
                                <div className="p-3 rounded-full bg-purple-100">
                                    <Package className="w-6 h-6 text-purple-600" />
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                                {data.total_cargo_logs} {isEn ? 'cargo logs' : 'καταγραφές φορτίου'}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Recommendations */}
            {data && data.recommendations.length > 0 && (
                <Card className="border-l-4 border-l-[#1B7EB5]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-[#1B7EB5]" />
                            {isEn ? 'Recommendations' : 'Συστάσεις'}
                            <InfoTooltip 
                                content={isEn 
                                    ? "AI-generated suggestions based on your fleet's usage patterns. Implementing these can help reduce costs and improve efficiency."
                                    : "Προτάσεις που δημιουργούνται από AI βάσει των προτύπων χρήσης του στόλου σας. Η εφαρμογή τους μπορεί να βοηθήσει στη μείωση του κόστους."
                                }
                            />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-2">
                            {data.recommendations.map((rec, index) => (
                                <li key={index} className="flex items-start gap-2 text-slate-700">
                                    <span>{rec}</span>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            {/* Tabs for Details */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="vehicles" className="flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        {isEn ? 'Vehicle Utilization' : 'Χρήση Οχημάτων'}
                    </TabsTrigger>
                    <TabsTrigger value="drivers" className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {isEn ? 'Driver Efficiency' : 'Αποδοτικότητα Οδηγών'}
                    </TabsTrigger>
                    <TabsTrigger value="cargo" className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        {isEn ? 'Cargo Capacity' : 'Χωρητικότητα Φορτίου'}
                    </TabsTrigger>
                </TabsList>

                {/* Vehicle Utilization Tab */}
                <TabsContent value="vehicles">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {isEn ? 'Vehicle Utilization Report' : 'Αναφορά Χρήσης Οχημάτων'}
                                <InfoTooltip 
                                    content={isEn 
                                        ? "Shows how often each vehicle is used. Utilization = Days with check-ins ÷ Total days. Vehicles below 40% are flagged as underused."
                                        : "Δείχνει πόσο συχνά χρησιμοποιείται κάθε όχημα. Χρήση = Ημέρες με check-in ÷ Σύνολο ημερών. Οχήματα κάτω από 40% σημειώνονται ως υποχρησιμοποιημένα."
                                    }
                                />
                            </CardTitle>
                            <CardDescription>
                                {isEn 
                                    ? `Analysis for the last ${selectedPeriod} days`
                                    : `Ανάλυση για τις τελευταίες ${selectedPeriod} μέρες`}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Vehicle' : 'Όχημα'}</th>
                                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Days Used' : 'Ημέρες Χρήσης'}</th>
                                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Utilization' : 'Χρήση'}</th>
                                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Total KM' : 'Σύνολο ΧΛΜ'}</th>
                                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Status' : 'Κατάσταση'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.vehicle_utilizations.map((vehicle, index) => (
                                            <tr key={vehicle.vehicle_id} className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="py-3 px-4">
                                                    <span className="font-medium text-slate-800">{vehicle.vehicle_info}</span>
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <span className="font-medium">{vehicle.days_used}</span>
                                                    <span className="text-slate-500"> / {vehicle.total_days}</span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Progress value={vehicle.utilization_percentage} className="w-20 h-2" />
                                                        <span className={`font-medium ${vehicle.utilization_percentage >= 70 ? 'text-green-600' : vehicle.utilization_percentage >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                            {vehicle.utilization_percentage}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 text-center text-slate-600">
                                                    {vehicle.total_km.toLocaleString()} km
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    {getUtilizationBadge(vehicle.status)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Driver Efficiency Tab */}
                <TabsContent value="drivers">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {isEn ? 'Driver Fuel Efficiency' : 'Αποδοτικότητα Καυσίμου Οδηγών'}
                                <InfoTooltip 
                                    content={isEn 
                                        ? "Compares how efficiently each driver uses fuel. Calculated as Total KM ÷ Total Liters. Higher km/L = more efficient driving."
                                        : "Συγκρίνει πόσο αποδοτικά χρησιμοποιεί κάθε οδηγός τα καύσιμα. Υπολογίζεται ως Σύνολο ΧΛΜ ÷ Σύνολο Λίτρων. Υψηλότερο km/L = πιο αποδοτική οδήγηση."
                                    }
                                />
                            </CardTitle>
                            <CardDescription>
                                {isEn 
                                    ? 'Compare fuel efficiency across your drivers'
                                    : 'Συγκρίνετε την αποδοτικότητα καυσίμου μεταξύ των οδηγών σας'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {data?.driver_efficiencies.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-slate-200">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Driver' : 'Οδηγός'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Total KM' : 'Σύνολο ΧΛΜ'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Fuel Used (L)' : 'Καύσιμο (L)'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Efficiency' : 'Αποδοτικότητα'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Rating' : 'Αξιολόγηση'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.driver_efficiencies.map((driver, index) => (
                                                <tr key={driver.driver_id} className="border-b border-slate-100 hover:bg-slate-50">
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center gap-2">
                                                            {index === 0 && <span className="text-yellow-500">🏆</span>}
                                                            <span className="font-medium text-slate-800">{driver.driver_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 text-center text-slate-600">
                                                        {driver.total_km.toLocaleString()} km
                                                    </td>
                                                    <td className="py-3 px-4 text-center text-slate-600">
                                                        {driver.total_fuel_liters.toFixed(1)} L
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className="font-bold text-[#1B7EB5]">{driver.avg_km_per_liter}</span>
                                                        <span className="text-slate-500"> km/L</span>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        {getEfficiencyBadge(driver.efficiency_rating)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-slate-500">
                                    <Fuel className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>{isEn ? 'No fuel efficiency data available' : 'Δεν υπάρχουν διαθέσιμα δεδομένα αποδοτικότητας καυσίμου'}</p>
                                    <p className="text-sm mt-2">{isEn ? 'Add fuel logs and check-ins to see driver comparisons' : 'Προσθέστε καταγραφές καυσίμου και check-in για να δείτε συγκρίσεις'}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Cargo Capacity Tab */}
                <TabsContent value="cargo">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                {isEn ? 'Cargo Capacity History' : 'Ιστορικό Χωρητικότητας Φορτίου'}
                                <InfoTooltip 
                                    content={isEn 
                                        ? "AI analyzes photos of cargo areas to estimate capacity utilization. Higher % means the vehicle is being used efficiently. Drivers can upload cargo photos during check-in."
                                        : "Το AI αναλύει φωτογραφίες του χώρου φορτίου για να εκτιμήσει τη χρήση χωρητικότητας. Υψηλότερο % σημαίνει ότι το όχημα χρησιμοποιείται αποδοτικά."
                                    }
                                />
                            </CardTitle>
                            <CardDescription>
                                {isEn 
                                    ? 'AI-analyzed cargo photos from driver check-ins'
                                    : 'Φωτογραφίες φορτίου αναλυμένες με AI από check-in οδηγών'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {cargoHistory.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-slate-200">
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Date' : 'Ημερομηνία'}</th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Vehicle' : 'Όχημα'}</th>
                                                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Driver' : 'Οδηγός'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Capacity' : 'Χωρητικότητα'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Load Type' : 'Τύπος Φορτίου'}</th>
                                                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">{isEn ? 'Efficiency' : 'Αποδοτικότητα'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cargoHistory.map((log, index) => (
                                                <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                                                    <td className="py-3 px-4 text-slate-600">
                                                        {new Date(log.timestamp).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 px-4 font-medium text-slate-800">
                                                        {log.vehicle_info}
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-600">
                                                        {log.driver_name}
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <Progress value={log.capacity_percentage} className="w-16 h-2" />
                                                            <span className="font-bold text-[#1B7EB5]">{log.capacity_percentage}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <Badge variant="outline">{log.load_type}</Badge>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <Badge className={
                                                            log.space_efficiency === 'optimal' ? 'bg-green-100 text-green-800' :
                                                            log.space_efficiency === 'good' ? 'bg-blue-100 text-blue-800' :
                                                            log.space_efficiency === 'poor' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-red-100 text-red-800'
                                                        }>
                                                            {log.space_efficiency}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-slate-500">
                                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>{isEn ? 'No cargo capacity data yet' : 'Δεν υπάρχουν ακόμα δεδομένα χωρητικότητας φορτίου'}</p>
                                    <p className="text-sm mt-2">{isEn ? 'Drivers can upload cargo photos during check-in to track loading efficiency' : 'Οι οδηγοί μπορούν να ανεβάσουν φωτογραφίες φορτίου κατά το check-in'}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
